import React, { useCallback, useEffect, useState } from 'react';
import { apiCall } from './_shared';

const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const ScheduleRow = ({ row, days, canManage, onSaved, onError }) => {
  const [starts, setStarts] = useState(row.starts || {});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (d, v) => { setStarts((s) => ({ ...s, [d]: v })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await apiCall(`/api/admin/attendance/schedule/${row.username}`, { method: 'PUT', body: JSON.stringify({ starts }) }); setDirty(false); onSaved(row.username); } catch (e) { onError(e.message); }
    setSaving(false);
  };
  const cells = days.map((d) => (
    <td key={d} className="py-1 px-0.5">
      <input type="time" value={starts[d] || ''} disabled={!canManage} onChange={(e) => set(d, e.target.value)} className="border rounded px-1 py-0.5 text-[11px] w-[74px] disabled:bg-slate-50" data-testid={`sched-${row.username}-${d}`} />
    </td>
  ));
  return (
    <tr className="border-t" data-testid={`sched-row-${row.username}`}>
      <td className="py-1 pr-2 font-semibold text-slate-900 text-xs whitespace-nowrap">{row.username}<br /><span className="text-[10px] text-slate-500 font-normal">{(row.role || '').replace('_', ' ')}</span></td>
      {cells}
      <td className="py-1 pl-1">{canManage && <button type="button" onClick={save} disabled={!dirty || saving} className="text-[11px] bg-slate-900 text-white rounded px-2 py-1 disabled:opacity-40" data-testid={`sched-save-${row.username}`}>{saving ? '…' : 'Save'}</button>}</td>
    </tr>
  );
};

export const ScheduleEditor = ({ canManage, onClose }) => {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { setData(await apiCall('/api/admin/attendance/schedule')); } catch (e) { setMsg(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const runCheck = async () => {
    try { const r = await apiCall('/api/admin/attendance/late-check', { method: 'POST' }); setMsg(r.late.length ? `Late now: ${r.late.join(', ')}${r.alerted.length ? ` — manager alerted for ${r.alerted.join(', ')}` : ' (already alerted)'}` : 'Nobody is late right now.'); } catch (e) { setMsg(e.message); }
  };
  const days = data?.days || [];
  const heads = days.map((d) => <th key={d} className="pb-1 px-0.5 text-center">{DAY_LABEL[d]}</th>);
  const rows = (data?.rows || []).map((r) => <ScheduleRow key={r.username} row={r} days={days} canManage={canManage} onSaved={(u) => setMsg(`Saved planned starts for ${u}.`)} onError={setMsg} />);
  return (
    <div className="border rounded-xl p-3 bg-slate-50 mb-3" data-testid="att-schedule">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <p className="text-xs text-slate-700 flex-1 min-w-[240px]">Planned shift start per weekday (leave empty for a day off). If an employee has not scanned <b>{data?.grace_minutes ?? 15} minutes</b> after the planned start, the manager gets a push — once per day.</p>
        {canManage && <button type="button" onClick={runCheck} className="text-xs border rounded px-2.5 py-1.5 hover:bg-white" data-testid="att-late-check">Check now</button>}
        <button type="button" onClick={onClose} className="text-xs underline text-slate-600" data-testid="att-schedule-hide">Hide</button>
      </div>
      {msg && <p className="text-xs text-slate-700 mb-2" data-testid="att-schedule-msg">{msg}</p>}
      <div className="overflow-x-auto">
        <table className="text-xs" data-testid="att-schedule-table">
          <thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="pb-1 pr-2">Employee</th>{heads}<th /></tr></thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
};
